/**
 * V37.40 — Onglet « Suivi SEPA » : tableau de suivi des paiements SEPA
 * GoCardless rattaches aux souscriptions DOS DOMIUM.
 *
 * Endpoint : GET /api/domium/subscribe/admin/sepa-tracking
 *   ?status=all|pending|partial|completed|failed|active&reference=
 */
import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  AlertCircle, CheckCircle, ChevronDown, Clock, Landmark, Loader2,
  RefreshCw, Search, XCircle,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { API, formatDate, formatMoney } from './constants';

const INSTALLMENT_STATUS = {
  pending_submission: { label: 'A soumettre', color: 'bg-gray-100 text-gray-700 border-gray-300', Icon: Clock },
  submitted: { label: 'Soumis', color: 'bg-blue-100 text-blue-700 border-blue-300', Icon: Clock },
  confirmed: { label: 'Confirme', color: 'bg-indigo-100 text-indigo-700 border-indigo-300', Icon: CheckCircle },
  paid: { label: 'Paye', color: 'bg-emerald-100 text-emerald-700 border-emerald-300', Icon: CheckCircle },
  paid_out: { label: 'Paye', color: 'bg-emerald-100 text-emerald-700 border-emerald-300', Icon: CheckCircle },
  failed: { label: 'Echec', color: 'bg-red-100 text-red-700 border-red-300', Icon: XCircle },
  chargedback: { label: 'Retro-debit', color: 'bg-orange-100 text-orange-700 border-orange-300', Icon: AlertCircle },
  cancelled: { label: 'Annule', color: 'bg-gray-100 text-gray-500 border-gray-300', Icon: XCircle },
};

function statusBadge(status) {
  const cfg = INSTALLMENT_STATUS[status] || INSTALLMENT_STATUS.pending_submission;
  const Icon = cfg.Icon;
  return (
    <Badge variant="outline" className={cn('text-[10px] gap-1', cfg.color)}>
      <Icon className="h-3 w-3" />
      {cfg.label}
    </Badge>
  );
}

export default function SuiviSEPATab({ getAuthHeader }) {
  const [filters, setFilters] = useState({ status: 'all', reference: '' });
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [expandedRef, setExpandedRef] = useState(null);
  const [retrying, setRetrying] = useState({});

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status && filters.status !== 'all') params.append('status', filters.status);
      if (filters.reference) params.append('reference', filters.reference);
      const qs = params.toString();
      const url = `${API}/domium/subscribe/admin/sepa-tracking${qs ? `?${qs}` : ''}`;
      const res = await axios.get(url, { headers: getAuthHeader() });
      setEntries(res.data?.entries || []);
      setTotal(res.data?.total || 0);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur chargement suivi SEPA');
    } finally {
      setLoading(false);
    }
  }, [filters, getAuthHeader]);

  const handleRetry = async (souscription_id, index) => {
    const key = `${souscription_id}-${index}`;
    setRetrying((p) => ({ ...p, [key]: true }));
    try {
      await axios.post(
        `${API}/gocardless/admin/plans/${souscription_id}/installments/${index}/retry`,
        {},
        { headers: getAuthHeader() },
      );
      toast.success(`Prelevement n°${index} relance`);
      fetchEntries();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur relance prelevement');
    } finally {
      setRetrying((p) => ({ ...p, [key]: false }));
    }
  };

  // KPIs
  const kpis = entries.reduce(
    (acc, e) => {
      acc.totalEur += e.total_eur || 0;
      acc.paidEur += e.paid_eur || 0;
      if (e.is_completed) acc.completed += 1;
      if (e.is_failed) acc.withFailures += 1;
      if (e.is_pending_only) acc.pendingOnly += 1;
      return acc;
    },
    { totalEur: 0, paidEur: 0, completed: 0, withFailures: 0, pendingOnly: 0 },
  );

  return (
    <div className="space-y-4" data-testid="suivi-sepa-tab">
      <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="flex items-center gap-2 text-blue-900">
              <Landmark className="h-5 w-5" />
              Suivi SEPA GoCardless — DOS DOMIUM
            </CardTitle>
            <Badge className="bg-blue-700 text-white border-blue-900 font-semibold">
              {total} mandat{total > 1 ? 's' : ''} SEPA
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-blue-800">
            Tableau de suivi des mandats SEPA et prelevements GoCardless rattaches aux
            souscriptions DOS DOMIUM. Vous pouvez relancer un prelevement en echec
            depuis l&apos;onglet.
          </p>
        </CardContent>
      </Card>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <div className="p-3 rounded-lg border bg-white">
          <p className="text-[10px] text-gray-400 uppercase">Total mandate</p>
          <p className="text-lg font-bold text-gray-900">{total}</p>
        </div>
        <div className="p-3 rounded-lg border bg-white">
          <p className="text-[10px] text-gray-400 uppercase">A debuter</p>
          <p className="text-lg font-bold text-gray-700">{kpis.pendingOnly}</p>
        </div>
        <div className="p-3 rounded-lg border bg-white">
          <p className="text-[10px] text-gray-400 uppercase">Soldes</p>
          <p className="text-lg font-bold text-emerald-700">{kpis.completed}</p>
        </div>
        <div className="p-3 rounded-lg border bg-white">
          <p className="text-[10px] text-gray-400 uppercase">Avec echecs</p>
          <p className="text-lg font-bold text-red-700">{kpis.withFailures}</p>
        </div>
        <div className="p-3 rounded-lg border bg-white">
          <p className="text-[10px] text-gray-400 uppercase">Encaisse</p>
          <p className="text-lg font-bold text-indigo-700">{formatMoney(kpis.paidEur)} EUR</p>
        </div>
      </div>

      {/* Filtres */}
      <Card>
        <CardContent className="py-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <div>
              <Label className="text-xs text-gray-500">Statut</Label>
              <Select value={filters.status} onValueChange={(v) => setFilters((p) => ({ ...p, status: v }))}>
                <SelectTrigger data-testid="sepa-status-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  <SelectItem value="pending">A debuter</SelectItem>
                  <SelectItem value="active">Mandat actif</SelectItem>
                  <SelectItem value="partial">En cours</SelectItem>
                  <SelectItem value="completed">Soldes</SelectItem>
                  <SelectItem value="failed">Avec echecs</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs text-gray-500">Reference souscription</Label>
              <Input
                placeholder="DOM-..."
                value={filters.reference}
                onChange={(e) => setFilters((p) => ({ ...p, reference: e.target.value }))}
                data-testid="sepa-reference-filter"
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={fetchEntries} disabled={loading} data-testid="sepa-refresh">
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Search className="h-4 w-4 mr-1" />}
                Charger
              </Button>
              <Button onClick={() => { setFilters({ status: 'all', reference: '' }); }} variant="outline">
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Liste */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-12 text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-blue-500" />
              <p className="text-sm text-gray-500 mt-2">Chargement...</p>
            </div>
          ) : entries.length === 0 ? (
            <div className="py-12 text-center">
              <Landmark className="h-12 w-12 mx-auto text-gray-300 mb-2" />
              <p className="text-gray-700 font-medium">Aucun mandat SEPA trouve</p>
              <p className="text-xs text-gray-400 mt-2">
                Cliquez sur <strong>« Charger »</strong> ou activez un mandat SEPA depuis l&apos;onglet Paiements.
              </p>
            </div>
          ) : (
            <div className="divide-y" data-testid="sepa-list">
              {entries.map((e, idx) => {
                const isExp = expandedRef === e.reference;
                const progress = e.total_eur > 0 ? (e.paid_eur / e.total_eur) * 100 : 0;
                return (
                  <div key={`${e.reference}-${idx}`} data-testid={`sepa-row-${idx}`}>
                    <button
                      onClick={() => setExpandedRef(isExp ? null : e.reference)}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50"
                    >
                      <Landmark className={cn('h-4 w-4 shrink-0', e.is_completed ? 'text-emerald-500' : e.is_failed ? 'text-red-500' : 'text-blue-500')} />
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-sm text-gray-900 truncate">{e.souscripteur}</p>
                        <p className="text-[10px] text-gray-400 font-mono truncate">{e.reference} · {e.customer_email || '—'}</p>
                      </div>
                      <Badge className={cn('text-[10px] shrink-0', e.mandate_status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600')}>
                        {e.mandate_status || 'unknown'}
                      </Badge>
                      <div className="hidden sm:flex items-center gap-2 w-28 shrink-0">
                        <div className="h-1.5 flex-1 bg-gray-200 rounded-full overflow-hidden">
                          <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${progress}%` }} />
                        </div>
                        <span className="text-[10px] text-gray-500 tabular-nums">{e.n_paid}/{e.n_total}</span>
                      </div>
                      <span className="text-sm font-bold text-blue-700 tabular-nums shrink-0">
                        {formatMoney(e.paid_eur)} / {formatMoney(e.total_eur)} EUR
                      </span>
                      <ChevronDown className={cn('h-4 w-4 text-gray-400 transition-transform', isExp && 'rotate-180')} />
                    </button>

                    {isExp && (
                      <div className="px-4 pb-4 pt-1 bg-gray-50/50">
                        <div className="bg-white rounded-lg border overflow-x-auto">
                          <table className="w-full text-xs">
                            <thead className="bg-gray-50 border-b">
                              <tr>
                                <th className="p-2 text-left">N°</th>
                                <th className="p-2 text-left">Echeance</th>
                                <th className="p-2 text-right">Montant</th>
                                <th className="p-2 text-center">Statut</th>
                                <th className="p-2 text-left">Paiement GC</th>
                                <th className="p-2 text-left">Echec</th>
                                <th className="p-2 text-right">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {e.installments.map((inst) => {
                                const canRetry = ['failed', 'chargedback'].includes(inst.status);
                                const retryKey = `${e.reference}-${inst.index}`;
                                return (
                                  <tr key={inst.index} className="border-b last:border-b-0">
                                    <td className="p-2 font-mono text-gray-500">{inst.index}</td>
                                    <td className="p-2">{formatDate(inst.due_date)}</td>
                                    <td className="p-2 text-right font-mono">{formatMoney(inst.amount_eur)} EUR</td>
                                    <td className="p-2 text-center">{statusBadge(inst.status)}</td>
                                    <td className="p-2 font-mono text-[10px] text-gray-400">{inst.payment_id || '—'}</td>
                                    <td className="p-2 text-red-600 text-[10px]">{inst.failure_reason || '—'}</td>
                                    <td className="p-2 text-right">
                                      {canRetry && (
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          className="h-6 text-[10px] border-orange-300 text-orange-700"
                                          disabled={retrying[retryKey]}
                                          onClick={() => handleRetry(e.reference, inst.index)}
                                          data-testid={`sepa-retry-${idx}-${inst.index}`}
                                        >
                                          {retrying[retryKey] ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                                          Relancer
                                        </Button>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
